import React, { useState } from 'react';
import { useMisNotas } from '../hooks/useMisNotas';
import { CursoEstudianteItem } from '../types/matricula.types';
import { PeriodoResponse } from '../types/periodo.types';
import { NotaActividadResponse } from '../types/notaActividad.types';
import { AñoEscolar } from '../types/usuario.types';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlBoleta } from 'utils/documentoPdf';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// MisNotasPage – Vista de notas para el estudiante
//   1. Selecciona año → ve cursos
//   2. Selecciona curso → ve periodos
//   3. Selecciona periodo → ve notas detalladas
// ============================================================

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_BACK = 'M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const CARD_ACCENTS = [
  'border-t-blue-500', 'border-t-purple-500', 'border-t-green-500',
  'border-t-amber-500', 'border-t-pink-500', 'border-t-indigo-500',
  'border-t-teal-500', 'border-t-orange-500',
];

// ── Selector de año escolar ─────────────────────────────────
const AnioSelector: React.FC<{
  años: AñoEscolar[];
  selected: number | null;
  onChange: (id: number) => void;
}> = ({ años, selected, onChange }) => (
  <div className="flex items-center gap-2">
    <label className="text-sm font-medium text-slate-600">Año:</label>
    <select
      value={selected ?? ''}
      onChange={e => onChange(Number(e.target.value))}
      className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white
                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
    >
      {años.map(a => (
        <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
      ))}
    </select>
  </div>
);

// ── Grid de cursos ──────────────────────────────────────────
const CursosGrid: React.FC<{
  cursos: CursoEstudianteItem[];
  onSelect: (c: CursoEstudianteItem) => void;
}> = ({ cursos, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {cursos.map((c, idx) => (
      <button
        key={c.asignacionAcademicaId}
        onClick={() => onSelect(c)}
        className={`text-left bg-white rounded-2xl border border-slate-100 shadow-sm
          border-t-4 ${CARD_ACCENTS[idx % CARD_ACCENTS.length]}
          transition-all hover:shadow-md hover:-translate-y-0.5 p-5`}
      >
        <h3 className="font-bold text-slate-800 text-base leading-tight mb-2">{c.cursoNombre}</h3>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400 flex-shrink-0">
            <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
          </svg>
          <span>{c.docenteNombres} {c.docenteApellidos}</span>
        </div>
      </button>
    ))}
  </div>
);

// ── Selector de periodo (pills) ─────────────────────────────
const PeriodoPills: React.FC<{
  periodos: PeriodoResponse[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}> = ({ periodos, loading, selectedId, onSelect }) => {
  if (loading) return <SpinnerIcon />;
  if (periodos.length === 0) {
    return <p className="text-slate-400 text-sm">No hay periodos configurados para este año escolar</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {periodos.map(p => (
        <button
          key={p.idPeriodo}
          onClick={() => onSelect(p.idPeriodo)}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
            ${selectedId === p.idPeriodo
              ? 'bg-blue-700 text-white shadow-sm'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
        >
          {p.nombre}
        </button>
      ))}
    </div>
  );
};

// ── Tabla de notas con resumen ──────────────────────────────
const NotasTable: React.FC<{
  notas: NotaActividadResponse[];
  loading: boolean;
}> = ({ notas, loading }) => {
  if (loading) {
    return <div className="flex justify-center py-12"><SpinnerIcon /></div>;
  }

  if (notas.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
          className="w-10 h-10 text-slate-200 mx-auto mb-3">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
        </svg>
        <p className="text-slate-400 text-sm font-medium">Sin notas registradas en este periodo</p>
      </div>
    );
  }

  const notasNum = notas.filter(n => n.nota != null).map(n => n.nota as number);
  const tieneNumericas = notasNum.length > 0;
  const promedio = tieneNumericas ? notasNum.reduce((sum, v) => sum + v, 0) / notasNum.length : null;
  const notaMax  = tieneNumericas ? Math.max(...notasNum) : null;
  const notaMin  = tieneNumericas ? Math.min(...notasNum) : null;

  return (
    <div className="space-y-4">
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 text-center">
          <p className={`text-2xl font-bold ${promedio != null ? (promedio >= 11 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}`}>
            {promedio != null ? promedio.toFixed(2) : '—'}
          </p>
          <p className="text-slate-400 text-xs mt-1">Promedio</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{notaMax != null ? notaMax.toFixed(2) : '—'}</p>
          <p className="text-slate-400 text-xs mt-1">Nota más alta</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{notaMin != null ? notaMin.toFixed(2) : '—'}</p>
          <p className="text-slate-400 text-xs mt-1">Nota más baja</p>
        </div>
      </div>

      {/* Tabla de detalle */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-6 py-3 text-left  text-xs font-bold text-slate-500 uppercase tracking-wide w-8">#</th>
              <th className="px-6 py-3 text-left  text-xs font-bold text-slate-500 uppercase tracking-wide">Actividad</th>
              <th className="px-6 py-3 text-left  text-xs font-bold text-slate-500 uppercase tracking-wide">Curso</th>
              <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Nota</th>
              <th className="px-6 py-3 text-left  text-xs font-bold text-slate-500 uppercase tracking-wide">Observación</th>
              <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {notas.map((n, idx) => (
              <tr key={n.idNotaActividad} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-sm text-slate-400">{idx + 1}</td>
                <td className="px-6 py-4 text-sm font-semibold text-slate-800">{n.descripcion}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{n.cursoNombre}</td>
                <td className="px-6 py-4 text-center">
                  {n.notaLetra != null ? (
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                      {n.notaLetra}
                    </span>
                  ) : (
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold
                      ${(n.nota ?? 0) >= 11 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {n.nota?.toFixed(2) ?? '—'}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                  {n.observacion || <span className="text-slate-300 italic">—</span>}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500 text-center">{n.fechaRegistro}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Componente principal ────────────────────────────────────
const MisNotasPage: React.FC = () => {
  const {
    añosEscolares, anioSeleccionado,
    cursosData, loadingCursos, error,
    selectedCurso, periodos, selectedPeriodoId, loadingPeriodos,
    notas, loadingNotas, matriculaId,
    cambiarAnio, selectCurso, selectPeriodo, volverACursos,
  } = useMisNotas();

  const [loadingBoleta, setLoadingBoleta] = useState(false);
  const [boletaError, setBoletaError]     = useState<string | null>(null);

  const handleDescargarBoleta = async () => {
    if (!matriculaId || !selectedPeriodoId) return;
    setLoadingBoleta(true);
    setBoletaError(null);
    try {
      const data = await documentoService.getBoleta(matriculaId, selectedPeriodoId);
      const nombre = cursosData
        ? `${cursosData.estudianteApellidos ?? ''} ${cursosData.estudianteNombres ?? ''}`.trim()
        : 'Estudiante';
      exportarPDF(`Boleta de Notas — ${nombre}`, htmlBoleta(data), 'portrait');
    } catch (e: unknown) {
      setBoletaError(e instanceof Error ? e.message : 'Error al generar la Boleta');
    } finally {
      setLoadingBoleta(false);
    }
  };

  const cursos = cursosData?.cursos ?? [];

  // ── Loading ───────────────────────────────────────────────
  if (loadingCursos && !cursosData) {
    return (
      <div className="flex justify-center items-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  // ── Vista detalle de un curso ─────────────────────────────
  if (selectedCurso) {
    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={volverACursos}
            className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
            <Ico d={D_BACK} cls="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-800">{selectedCurso.cursoNombre}</h1>
            <p className="text-slate-400 text-sm">
              Docente: {selectedCurso.docenteNombres} {selectedCurso.docenteApellidos}
            </p>
          </div>
        </div>

        {/* Selector de periodo */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Periodo</p>
          <PeriodoPills
            periodos={periodos}
            loading={loadingPeriodos}
            selectedId={selectedPeriodoId}
            onSelect={selectPeriodo}
          />
        </div>

        {/* Boleta + Notas */}
        {selectedPeriodoId && (
          <>
            {matriculaId && (
              <div className="flex justify-end">
                <button
                  onClick={handleDescargarBoleta}
                  disabled={loadingBoleta}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
                >
                  {loadingBoleta ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
                    </svg>
                  )}
                  Descargar Boleta
                </button>
              </div>
            )}
            {boletaError && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-800 text-sm">
                {boletaError}
              </div>
            )}
            <NotasTable notas={notas} loading={loadingNotas} />
          </>
        )}
      </div>
    );
  }

  // ── Vista principal: selección de cursos ──────────────────
  return (
    <div className="space-y-6">

      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
        <p className="text-blue-200 text-sm font-medium mb-1">Mis Notas</p>
        <h2 className="text-2xl font-bold">
          {cursosData
            ? `${cursosData.estudianteNombres} ${cursosData.estudianteApellidos}`
            : 'Cargando...'}
        </h2>
        {cursosData && (
          <p className="text-blue-200 text-sm mt-1">
            {cursosData.gradoNombre} — Sección {cursosData.seccionNombre} &bull; Año {cursosData.anio}
          </p>
        )}
      </div>

      {/* Selector de año + info */}
      <div className="flex items-center justify-between">
        <p className="text-slate-500 text-sm">
          Selecciona un curso para ver tus notas
        </p>
        <AnioSelector
          años={añosEscolares}
          selected={anioSeleccionado}
          onChange={cambiarAnio}
        />
      </div>

      {/* Contenido */}
      {loadingCursos ? (
        <div className="flex justify-center py-12"><SpinnerIcon /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : cursos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
          </svg>
          <p className="text-slate-400 text-sm font-medium">No se encontraron cursos</p>
          <p className="text-slate-300 text-xs mt-1">No tienes cursos asignados para este año escolar</p>
        </div>
      ) : (
        <CursosGrid cursos={cursos} onSelect={selectCurso} />
      )}
    </div>
  );
};

export default MisNotasPage;
